import { useMemo, useState, type ComponentProps } from 'react';
import type { Client, Metafield } from '@forecastify/shared';
import { DataList, type DataListColumn, type DataListRow } from '@forecastify/ui';
import { useT } from '../../i18n';
import type { ListQuery } from '../use-list-query';
import { metafieldCells, metafieldColumns, metafieldFilters } from '../metafield-columns';
import { ListHeader, usePageHeader } from '../page-header';
import { ClientDialog, type ClientDraft } from './client-dialog';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Ügyfelek — `markup.html` 662–665. One `DataList`, and no bespoke table.    │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The mockup renders this view as a single `Shadcn.DataList` with columns, filters, search, a row
 * menu and a footer note. C3 built that component, so this screen supplies task-shaped data and
 * nothing else — CLAUDE.md's *"screens compose components; they do not define them"*.
 *
 * ⚠️ **Search and filtering happen HERE, in memory, and that is a divergence worth stating.**
 * CLAUDE.md §3 says filtering is server-side and applied before permission trimming. It is
 * in-memory here because `GET /clients` returns the whole set — a company's client list is bounded
 * and the screen renders every row, which `delivery.controller.ts` records as the reason there is
 * no cursor envelope either. **Both change together**: the day this list paginates, the search box
 * and the two filters become query parameters, or the filter silently searches one page.
 *
 * ⚠️ **Archived clients are IN the list, greyed rather than hidden.** The server returns them
 * deliberately: this list is where somebody goes to restore one, and a default filter that hid them
 * would make the restore path unreachable. The state filter defaults to *live*, so they are out of
 * the way without being gone.
 */
export function ClientsScreen({
  page,
  query,
  industries,
  clients,
  canManage,
  onCreate,
  onUpdate,
  onArchive,
  onArchiveMany,
  metafields,
  serverError,
  loading,
}: {
  /** Until the first answer: skeleton rows and no figures. */
  readonly loading?: boolean;
  /**
   * ⚠️ The page controls. Absent means the list is not paged — which is how every embedded copy of
   * this table stays unchanged (`state-rules.md` §6: consistent WITHIN a screen).
   */
  readonly page?: ComponentProps<typeof DataList>['page'];
  /**
   * ⚠️⚠️ **SEARCH, FILTERS AND SORT ARE THE ROUTE'S NOW, AND THEY USED TO BE THIS FILE'S.** Until
   * 9 Sep 2026 this screen held `query`, `industry` and `state` in its own `useState` and filtered
   * the array it was handed — CLAUDE.md §3's *"never fetch-then-filter-in-memory"*, and visibly
   * wrong the moment item 20 gave the list a real pager: the search box filtered the fifty rows on
   * screen and reported *"no matches"* for a client on page two. Item 35 moved them to the server;
   * `use-list-query.ts` is what holds them.
   */
  readonly query: ListQuery;
  /**
   * ⚠️ **From the server, not from `clients`.** Built out of the loaded page it would drop any
   * industry whose only client sits on page three — from its own filter.
   */
  readonly industries: readonly string[];
  readonly clients: readonly Client[];
  /** `client.manage`. UI gating is UX; the server checks again. */
  readonly canManage: boolean;
  /** 26 Oct 2026 · resolves to whether it landed; the dialog closes on `true` only. */
  readonly onCreate: (draft: ClientDraft) => Promise<boolean>;
  readonly onUpdate: (client: Client, draft: ClientDraft) => Promise<boolean>;
  readonly onArchive: (client: Client) => void;
  /**
   * ⚠️ **Item 72 · archive or restore a selection, and there is DELIBERATELY no bulk delete.** A
   * client has no delete path at all — the single-row action is archive — so a bulk *delete* would
   * mean deciding what happens to a client's projects in a toolbar menu. `bulkArchiveSchema`
   * carries the argument; CLAUDE.md rule 8 carries the rule.
   */
  readonly onArchiveMany?: (ids: readonly string[], archived: boolean) => void;
  /** The workspace's metafield definitions for this entity — passed straight to the dialog. */
  readonly metafields?: readonly Metafield[];
  readonly serverError: string | null;
}) {
  const t = useT();
  /**
   * ┌───────────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ THE CREATE ACTION IS IN THE TOOLBAR, NOT THE HEADER — item 25, 15 Sep.│
   * └───────────────────────────────────────────────────────────────────────────┘
   *
   * *"They all should look like contracts, one line with the search, not purple."* Contracts have
   * passed their create through `DataList`'s `actions` since C30 and every other list grew a purple
   * header CTA instead, so the same act looked like two different things depending on which list
   * you were standing in.
   *
   * ⚠️ **The header keeps the TITLE and nothing else.** A header action is right for a screen whose
   * primary act is not about a list — a task detail, a project — and wrong here, where the toolbar
   * already holds the search, the filters and the columns that the create belongs beside.
   */
  usePageHeader(() => <ListHeader title={t('title.clients')} />, []);

  const [editing, setEditing] = useState<Client | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  /* ⚠️ **A map, not a filter.** Every row the server sent is a row that matched — the search and
     the two filters are in the query now, and re-applying them here would be the second, quieter
     half of the bug item 35 removes. */
  const rows: DataListRow[] = useMemo(
    () =>
      clients.map((client) => ({
        id: client.id,
        cells: {
          name: {
            /* The colour is the client's own, as a dot. `design-system/readme.md`: colour is never
             * the only carrier, so the name is right beside it. */
            dot: client.color,
            text: client.name,
            bold: true,
            /* ⚠️⚠️ **THE ARCHIVED WORD MOVED TO ITS OWN COLUMN — item 57a.** It sat here as a
               `sub` under the name, which is where a client's CODE would belong and where a
               reader looks for more of the name rather than for a state. `muted` stays: a row
               that is archived should still read as archived at a glance. */
            muted: client.archivedAt !== null,
          },
          state: {
            text:
              client.archivedAt === null ? t('clients.state.live') : t('clients.state.archived'),
            muted: client.archivedAt !== null,
          },
          contact: { text: client.contactName ?? '–', muted: client.contactName === null },
          email: { text: client.contactEmail ?? '–', muted: true },
          industry: { text: client.industry },
          country: { text: client.countryCode, mono: true, muted: true },
          /* ⚠️ `mono` is what gives tabular numerals — the kit's binding rule for any figure in a
           * column. Right alignment is the COLUMN's property, not the cell's. */
          projects: { text: client.projectCount, mono: true },
          /* ⚠️ **Item 27 · the workspace's own fields**, for every definition whose `inList` is on
             — a switch the settings screen has always offered and no list has ever read. */
          ...metafieldCells(t, metafields ?? [], client.metafields),
        },
      })),
    [clients, metafields, t],
  );

  const columns: DataListColumn[] = [
    /* ⚠️ Every `sortKey` is a name in `CLIENT_SORT`, the server's catalogue (item 45, D3). ⚠️ The
       contact and email columns carry none: neither is a column anybody orders a client list by,
       and a header button that nobody presses is still a header button somebody can press. */
    { key: 'name', label: t('clients.column.name'), sortKey: 'name' },
    /* ⚠️⚠️ **THE SHORT CODE IS GONE ENTIRELY — `0104` DROPPED THE COLUMN.** Item 57b took it off
        this list and left the value in the create form and the search clause, because removing it
        was a migration and a decision about what identifies a client. That decision was taken on
        10 Sep 2026: a client is its NAME. Nothing was keyed on the code — no URL, no export, no
        import — so the whole of it went in one statement. */
    /* ⚠️ **Item 57a: a column, not a chip beside the name.** */
    { key: 'state', label: t('clients.column.state'), width: 110, sortKey: 'state' },
    { key: 'contact', label: t('clients.column.contact') },
    { key: 'email', label: t('clients.column.email') },
    { key: 'industry', label: t('clients.column.industry'), width: 130, sortKey: 'industry' },
    { key: 'country', label: t('clients.column.country'), width: 80, sortKey: 'country' },
    {
      key: 'projects',
      label: t('clients.column.projects'),
      width: 90,
      align: 'right',
      sortKey: 'projects',
    },
    /* ⚠️ **No `sortKey` on any of these** — `metafield-columns.ts` argues it: a metafield's value
       is a `jsonb` lookup with no index behind it, and the filter is how they are narrowed. */
    ...metafieldColumns(metafields ?? []),
  ];

  return (
    <div data-screen="clients" className="flex min-h-0 flex-1 flex-col">
      {/* ⚠️ Edge to edge: the mockup drops its `DataList` straight into the content area
      (`markup.html` 455, 462, 594), and the padding that was here is the gap on both
      sides that was reported. */}
      <div className="min-h-0 flex-1">
        <DataList
          columnsKey="clients"
          card={{
            title: 'name',
            context: 'industry',
            defaults: { badge: 'state', details: ['contact', 'email', 'projects', 'country'] },
          }}
          isLoading={loading === true}
          columns={columns}

          actions={
            canManage
              ? [
                  {
                    label: t('clients.action.create'),
                    icon: 'plus' as const,
                    primary: true,
                    onSelect: () => {
                      setEditing(null);
                      setDialogOpen(true);
                    },
                  },
                ]
              : []
          }
          rows={rows}
          sort={query.sort}
          onSortChange={query.onSortChange}
          bulkActions={
            onArchiveMany === undefined || !canManage
              ? undefined
              : [
                  {
                    label: t('clients.action.archive'),
                    icon: 'trash' as const,
                    destructive: true,
                    onSelect: (ids: string[]) => {
                      onArchiveMany(ids, true);
                    },
                  },
                  {
                    /* ⚠️ **The restore is the reason this list keeps archived rows at all**
                       (`client.service.ts`), so the bar offers it rather than only the archive. */
                    label: t('clients.action.restore'),
                    icon: 'refresh-cw' as const,
                    onSelect: (ids: string[]) => {
                      onArchiveMany(ids, false);
                    },
                  },
                ]
          }
          search={query.search}
          onSearchChange={query.setSearch}
          filters={[
            {
              key: 'state',
              label: t('clients.filter.state'),
              value: query.filters.state ?? '',
              onChange: (value) => {
                query.setFilter('state', value);
              },
              options: [
                { value: 'live', label: t('clients.state.live') },
                { value: 'archived', label: t('clients.state.archived') },
                { value: '', label: t('common.all') },
              ],
            },
            {
              key: 'industry',
              label: t('clients.column.industry'),
              value: query.filters.industry ?? '',
              onChange: (value) => {
                query.setFilter('industry', value);
              },
              options: industries.map((name) => ({ value: name, label: name })),
            },
            /* ⚠️ **Item 35 · the workspace's own fields**, for the two types that have a closed set
               of values to offer. `metafield-columns.ts` argues the restriction. */
            ...metafieldFilters(t, metafields ?? [], query.filters, query.setFilter),
          ]}
          rowMenu={
            canManage
              ? (id) => {
                  const client = clients.find((candidate) => candidate.id === id);
                  if (client === undefined) return [];
                  return [
                    {
                      label: t('common.edit'),
                      icon: 'pencil',
                      onSelect: () => {
                        setEditing(client);
                        setDialogOpen(true);
                      },
                    },
                    {
                      label:
                        client.archivedAt === null
                          ? t('clients.action.archive')
                          : t('clients.action.restore'),
                      /* ⚠️ `inbox`, because the icon set has no `archive` — and `IconName` is a
                       * union of the 78 names, so the wrong one is a compile error rather than a
                       * blank square nobody notices until they open this screen. */
                      icon: 'inbox',
                      onSelect: () => {
                        onArchive(client);
                      },
                    },
                  ];
                }
              : undefined
          }
          onRowClick={
            canManage
              ? (id) => {
                  const client = clients.find((candidate) => candidate.id === id);
                  if (client === undefined) return;
                  setEditing(client);
                  setDialogOpen(true);
                }
              : undefined
          }
          empty={{
            icon: 'building',
            title: t('clients.empty.title'),
            body: t('clients.empty.body'),
            actionLabel: canManage ? t('clients.action.create') : undefined,
            onAction: canManage
              ? () => {
                  setEditing(null);
                  setDialogOpen(true);
                }
              : undefined,
          }}
          {...(page === undefined ? {} : { page })}
          labels={listLabels(t)}
        />
      </div>

      <ClientDialog
        metafields={metafields}
        open={dialogOpen}
        editing={editing}
        existing={clients}
        serverError={serverError}
        onClose={() => {
          setDialogOpen(false);
        }}
        onSubmit={(draft) => {
          /* 26 Oct 2026 · it never closed — on success either — so a created client looked
             unsaved and a second click made a duplicate. Closed on success only: a refusal stays
             in the open form, under the fields it is about (the project dialog's 21 Oct rule). */
          void (editing === null ? onCreate(draft) : onUpdate(editing, draft)).then((landed) => {
            if (landed) setDialogOpen(false);
          });
        }}
        onArchive={
          editing === null
            ? undefined
            : () => {
                onArchive(editing);
              }
        }
      />
    </div>
  );
}

/**
 * ⚠️ **Every `DataList` label, in one place per screen.** The component is string-agnostic by
 * design — it reaches for no catalogue — so the caller passes fourteen strings. Grouping them here
 * keeps the JSX readable and keeps `i18n:check` looking at literal keys.
 */
export function listLabels(t: ReturnType<typeof useT>) {
  return {
    search: t('list.search'),
    filter: t('list.filter'),
    views: t('list.views'),
    columns: t('list.columns'),
    clearAll: t('list.clearAll'),
    searchOptions: t('list.searchOptions'),
    clearFilter: (label: string) => t('list.clearFilter', { label }),
    rangeFrom: t('list.rangeFrom'),
    rangeTo: t('list.rangeTo'),
    switchView: t('list.switchView'),
    more: t('list.more'),
    back: t('list.back'),
    close: t('common.close'),
    table: t('list.table'),
    selectAll: t('list.selectAll'),
    selectRow: t('list.selectRow'),
    selectedCount: (count: number) => t('list.selectedCount', { count }),
    clearSelection: t('list.clearSelection'),
    rowMenu: t('list.rowMenu'),
    sortBy: (label: string) => t('list.sortBy', { label }),
    loading: t('state.loading'),
    loadMore: t('list.loadMore'),
    /* ⚠️⚠️ **CHEVRONS NOW, AND THE NOTE THAT STOOD HERE ARGUED FOR WORDS.** It was right that a
       bare chevron pair is announced as *"button, button"* — so the words moved to `aria-label`
       rather than disappearing, and the space they took is where item 20's page selector sits. */
    previousPage: t('list.previousPage'),
    nextPage: t('list.nextPage'),
    pageSelector: t('list.pageSelector'),
    pageOf: (page: number, count: number) => t('list.pageOf', { page, count }),
    /* ⚠️ **"találat", not a bare number.** The figure counts what the filters left rather than what
       the table holds, and the word is the only place that reading is carried. */
    pageTotal: (total: number) => t('list.pageTotal', { total }),
    loadingMore: t('list.loadingMore'),
  };
}
